// Regras puras da tela Plano de ação (sem antd, para rodar no jest do CRA).
// Aqui só se decide o que MOSTRAR (botão, cor, contagem). Quem barra de verdade
// é o servidor (`gestao.plano_acao.*` + dono da tarefa/etapa).
import { dataAoMeioDia, dataIso, hojeIso, somarDiasIso } from '../../utils/dataFmt';
import type {
  CamposLinha, Diagnostico, Etapa, Informacao, LinhaProposta, Nota, Permissoes, StatusEtapa, StatusInfo, StatusTarefa, Tarefa,
} from './api';

export { hojeIso };

/** Datas "AAAA-MM-DD" (ou ISO do banco) → "AAAA-MM-DD" sem deslocar por fuso. */
export const dia = (s: string | null | undefined): string | null => dataIso(s);

export type SituacaoDoCiclo = 'antes' | 'em_andamento' | 'depois' | 'sem_etapas';
export interface SemanaAtual { situacao: SituacaoDoCiclo; numero: number | null; total: number }

/**
 * Semana do ciclo pela data de hoje. A semana vale do início dela até o início
 * da seguinte (o fim de semana ainda é "a semana que passou"). Antes do início
 * do ciclo: `antes`; depois do fim da última: `depois`.
 */
export function semanaAtual(etapas: Pick<Etapa, 'numero' | 'inicio' | 'fim'>[], hoje: string): SemanaAtual {
  const lista = etapas.map((e) => ({ numero: e.numero, ini: dia(e.inicio), fim: dia(e.fim) }))
    .filter((e): e is { numero: number; ini: string; fim: string | null } => !!e.ini)
    .sort((a, b) => a.numero - b.numero);
  const total = lista.length;
  if (!total) return { situacao: 'sem_etapas', numero: null, total: 0 };
  if (hoje < lista[0].ini) return { situacao: 'antes', numero: null, total };
  const ultima = lista[total - 1];
  // Igual ao servidor: o fim de semana depois da última sexta ainda é "a semana que passou";
  // o ciclo só termina na segunda seguinte.
  if (ultima.fim && hoje > ultima.fim) {
    const dow = dataAoMeioDia(ultima.fim)?.getDay() ?? 5;
    const segunda = somarDiasIso(ultima.fim, ((8 - dow) % 7) || 7);
    if (!segunda || hoje >= segunda) return { situacao: 'depois', numero: null, total };
  }
  let atual = lista[0];
  for (const e of lista) if (e.ini <= hoje) atual = e;
  return { situacao: 'em_andamento', numero: atual.numero, total };
}

export function fraseDaSemana(s: SemanaAtual): string {
  if (s.situacao === 'sem_etapas') return 'Sem semanas cadastradas';
  if (s.situacao === 'antes') return 'O ciclo ainda não começou';
  if (s.situacao === 'depois') return 'Ciclo terminado';
  return `Semana ${s.numero} de ${s.total}`;
}

const ENCERRADA: StatusTarefa[] = ['concluida', 'cancelada', 'adiada'];

/**
 * Atrasada = tem prazo anterior a hoje, o ciclo está ligado e a tarefa ainda não
 * foi concluída, cancelada nem adiada. Ciclo em rascunho não tem tarefa de
 * ninguém para estar atrasada — igual ao servidor.
 */
export function tarefaAtrasada(t: Pick<Tarefa, 'prazo' | 'status'>, hoje: string, cicloLigado: boolean): boolean {
  const p = dia(t.prazo);
  if (!cicloLigado || !p || !t.status) return false;
  if (ENCERRADA.includes(t.status)) return false;
  return p < hoje;
}

/** Cancelada e adiada não contam (nem como feita, nem como pendente). */
export function tarefasQueContam<T extends Pick<Tarefa, 'status'>>(tarefas: T[]): T[] {
  return tarefas.filter((t) => t.status !== 'cancelada' && t.status !== 'adiada');
}

export function progresso(tarefas: Pick<Tarefa, 'status'>[]): { feitas: number; total: number; pct: number } {
  const v = tarefasQueContam(tarefas);
  const feitas = v.filter((t) => t.status === 'concluida').length;
  return { feitas, total: v.length, pct: v.length ? Math.round((feitas / v.length) * 100) : 0 };
}

export interface Resumo { pct: number; feitas: number; total: number; atrasadas: number; travadas: number }
export function resumoDoCiclo(etapas: Etapa[], hoje: string, cicloLigado: boolean): Resumo {
  const todas = etapas.flatMap((e) => e.tarefas);
  const p = progresso(todas);
  return {
    pct: p.pct, feitas: p.feitas, total: p.total,
    atrasadas: todas.filter((t) => tarefaAtrasada(t, hoje, cicloLigado)).length,
    travadas: etapas.filter((e) => e.status === 'travada').length,
  };
}

// ─── Prévia do "Atualizar do modelo": o servidor manda objetos, a tela mostra frases ───
const ALVO_PREVIA: Record<string, string> = { etapa: 'Semana', tarefa: 'Tarefa', informacao: 'Informação' };
const CAMPO_PREVIA: Record<string, string> = {
  titulo: 'título', descricao: 'descrição', pagina: 'página', pagina_config: 'configuração da página', status: 'situação',
  decisao_titulo: 'título da decisão', o_que_fazer: 'o que fazer', como_fazer: 'como fazer', entrega: 'entrega',
  quem_faz: 'quem faz', tempo_estimado: 'tempo estimado', item: 'item', onde: 'onde achar',
};
const valorPrevia = (v: unknown): string => {
  if (v == null || v === '') return 'vazio';
  const t = typeof v === 'object' ? 'ajustada' : String(v).replace(/\s+/g, ' ').trim();
  return t.length > 50 ? `${t.slice(0, 47)}...` : t;
};

/** Uma linha da prévia (mudança ou item deixado como está) em português. Texto puro passa como veio. */
export function fraseDaPrevia(i: unknown): string {
  if (typeof i === 'string') return i;
  const o = (i || {}) as Record<string, any>;
  const direto = o.descricao || o.texto || o.mensagem;
  if (typeof direto === 'string' && direto) return direto;
  const quem = `${ALVO_PREVIA[o.alvo] || 'Item'}${o.rotulo ? ` "${o.rotulo}"` : ''}`;
  if (o.campos && typeof o.campos === 'object') {
    const partes = Object.entries(o.campos as Record<string, { de?: unknown; para?: unknown }>)
      .map(([k, v]) => `${CAMPO_PREVIA[k] || k}: ${valorPrevia(v?.de)} → ${valorPrevia(v?.para)}`);
    return `${quem} — ${partes.join('; ')}`;
  }
  const campo = o.campo ? ` (${CAMPO_PREVIA[o.campo] || o.campo})` : '';
  return `${quem}${campo}${o.motivo ? ` — ${o.motivo}` : ''}`;
}

// ─── Quem pode o quê (só para esconder botão; o servidor é que barra) ───
const sou = (p: Permissoes, papel: string | null | undefined) => !!papel && p.papeis_do_usuario.includes(papel);

/** Gestor muda qualquer tarefa; participante só a própria e só com o ciclo ligado. */
export const podeMudarTarefa = (p: Permissoes, t: Pick<Tarefa, 'papel_responsavel'>, cicloLigado: boolean): boolean =>
  p.pode_gerir || (cicloLigado && p.pode_editar && sou(p, t.papel_responsavel));

/** Cancelada e adiada são decisão do gestor; a situação atual sempre aparece na lista. */
export function opcoesStatusTarefa(gestor: boolean, atual?: StatusTarefa | null): StatusTarefa[] {
  const base: StatusTarefa[] = ['pendente', 'em_andamento', 'concluida'];
  const todas: StatusTarefa[] = gestor ? [...base, 'adiada', 'cancelada'] : base;
  return atual && !todas.includes(atual) ? [...todas, atual] : todas;
}

/** Anotar: responsável, observadores e gestor. */
export const podeAnotar = (p: Permissoes, t: Pick<Tarefa, 'papel_responsavel' | 'papeis_observadores'>): boolean =>
  p.pode_gerir || (p.pode_editar && (sou(p, t.papel_responsavel) || t.papeis_observadores.some((o) => sou(p, o))));

/** Apagar nota: o autor ou o gestor. */
export const podeApagarNota = (gestor: boolean, email: string | undefined, n: Pick<Nota, 'por'>): boolean =>
  gestor || (!!email && String(n.por || '').toLowerCase() === email.toLowerCase());

export const podeMudarInformacao = (p: Permissoes, i: Pick<Informacao, 'papel_responsavel'>): boolean =>
  p.pode_gerir || (p.pode_editar && sou(p, i.papel_responsavel));
export const podeDecidirEtapa = (p: Permissoes, e: Pick<Etapa, 'papel_dono_decisao'>): boolean =>
  p.pode_gerir || (p.pode_editar && sou(p, e.papel_dono_decisao));
export const podeGerir = (p: Permissoes): boolean => p.pode_gerir;

/** Trava exige motivo escrito. */
export function travaValida(status: StatusEtapa, motivo: string): string | null {
  if (status === 'travada' && !motivo.trim()) return 'Escreva o motivo da trava.';
  return null;
}

// ─── Rótulos (português simples) ───
export const ROTULO_ETAPA: Record<StatusEtapa, { texto: string; cor: string }> = {
  nao_iniciada: { texto: 'Não iniciada', cor: 'default' },
  em_andamento: { texto: 'Em andamento', cor: 'blue' },
  concluida: { texto: 'Concluída', cor: 'green' },
  travada: { texto: 'Travada', cor: 'red' },
};
export const ROTULO_TAREFA: Record<StatusTarefa, string> = {
  pendente: 'Pendente', em_andamento: 'Em andamento', concluida: 'Concluída', cancelada: 'Cancelada', adiada: 'Adiada',
};
export const ROTULO_INFO: Record<StatusInfo, string> = { pendente: 'Pendente', em_andamento: 'Em andamento', pronto: 'Pronto' };
export const ROTULO_FONTE: Record<string, { texto: string; cor: string }> = {
  erp: { texto: 'ERP', cor: 'blue' },
  levantar: { texto: 'Levantar', cor: 'orange' },
  decisao: { texto: 'Decisão', cor: 'purple' },
};

/** "19/10 a 23/10", sem usar Date (não desloca por fuso). */
export function periodoCurto(ini: string | null, fim: string | null): string {
  const f = (s: string | null) => { const d = dia(s); return d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '—'; };
  return `${f(ini)} a ${f(fim)}`;
}

/** Segunda-feira da semana de `hoje` ("AAAA-MM-DD"); sugestão de início do ciclo. */
export function segundaDaSemana(hoje: string): string {
  const d = dataAoMeioDia(hoje);
  if (!d) return hoje;
  const dow = d.getDay(); // 0 = domingo
  return somarDiasIso(hoje, dow === 0 ? -6 : 1 - dow) || hoje;
}

// ─── Páginas por tarefa ───

/** Permissão que falta → frase simples para o aviso "sem acesso". */
export const ROTULO_PERMISSAO: Record<string, string> = {
  'financeiro.dre.ver': 'ver a DRE (Financeiro)',
  'comercial.pipeline.ver': 'ver o pipeline comercial',
  'comercial.negocio_privado.editar_dados': 'editar os dados do negócio',
};
export const frasePermissoes = (codigos: string[]): string =>
  codigos.map((c) => ROTULO_PERMISSAO[c] || c).join(' e ');

/** Tarefa com página própria (conteúdo com dados do ERP) em vez de só texto, notas e links. */
export const temPaginaEspecial = (t: Pick<Tarefa, 'pagina'>): boolean => !!t.pagina && t.pagina !== 'generica';

// ─── Página "20 últimas propostas decididas" (puras; a lista de motivos vem por parâmetro) ───
export const DIAGNOSTICOS: { value: Diagnostico; label: string }[] = [
  { value: 'houve', label: 'Houve reunião de diagnóstico' },
  { value: 'nao_houve', label: 'Não houve' },
  { value: 'dispensada', label: 'Dispensada (cliente conhecido)' },
];
export const LIMITE_TEXTO = 300;
const OUTRO = 'Outro motivo';

/**
 * Desfaz o texto gravado em `motivo_perda` ("Motivo — detalhe", ver `textoDoMotivo`
 * em ModalPerder) em motivo da lista + detalhe. Texto fora da lista (ex.: código
 * velho do Ploomes) vira "Outro motivo" com o texto inteiro no detalhe.
 */
export function separarMotivoPerda(texto: string | null | undefined, motivos: string[]): { motivo: string; detalhe: string } {
  const t = String(texto || '').trim();
  if (!t) return { motivo: '', detalhe: '' };
  const exato = motivos.find((m) => m === t);
  if (exato) return { motivo: exato, detalhe: '' };
  const prefixo = motivos.filter((m) => m !== OUTRO).find((m) => t.startsWith(`${m} — `));
  if (prefixo) return { motivo: prefixo, detalhe: t.slice(prefixo.length + 3).trim() };
  return { motivo: OUTRO, detalhe: t };
}

const vazioNulo = (v: unknown): string | null => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t ? t : null;
};
const CHAVES_LINHA: (keyof CamposLinha)[] = [
  'situacao_cliente', 'diagnostico', 'diagnostico_data', 'diagnostico_motivo', 'motivo_ganho', 'motivo_perda',
];
/** Vazio e nulo são a mesma coisa; data do banco em ISO completo vira "AAAA-MM-DD". */
const valorCampo = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v);
  return vazioNulo(/^\d{4}-\d{2}-\d{2}T/.test(s) ? dia(s) : s);
};

/** Só os campos que a pessoa mexeu em relação ao que a tela leu. */
export function camposAlterados(base: CamposLinha, edicao: Partial<CamposLinha>): Partial<CamposLinha> {
  const r: Record<string, string | null> = {};
  for (const k of CHAVES_LINHA) {
    if (!(k in edicao)) continue;
    const novo = valorCampo(edicao[k]);
    if (novo !== valorCampo(base[k])) r[k] = novo;
  }
  return r as Partial<CamposLinha>;
}

type LinhaBase = Pick<LinhaProposta, 'status' | 'data_envio'> & CamposLinha;

/** O que a tela confere antes de enviar (o servidor confere de novo). Lista vazia = pode salvar. */
export function errosDaLinha(alterados: Partial<CamposLinha>, linha: LinhaBase): string[] {
  const erros: string[] = [];
  const ef = { ...linha, ...alterados } as CamposLinha;
  const mexeu = (...ks: (keyof CamposLinha)[]) => ks.some((k) => k in alterados);
  if (mexeu('situacao_cliente') && (ef.situacao_cliente || '').length > LIMITE_TEXTO) {
    erros.push(`A situação do cliente passa de ${LIMITE_TEXTO} letras.`);
  }
  if (mexeu('diagnostico', 'diagnostico_data', 'diagnostico_motivo')) {
    if (ef.diagnostico === 'houve') {
      const d = dia(ef.diagnostico_data);
      const env = dia(linha.data_envio);
      if (!d) erros.push('Informe a data da reunião de diagnóstico.');
      else if (env && d > env) erros.push('A reunião de diagnóstico tem de ser antes do envio da proposta.');
    }
    if (ef.diagnostico === 'dispensada' && !vazioNulo(ef.diagnostico_motivo)) erros.push('Diga por que o diagnóstico foi dispensado.');
    if ((ef.diagnostico_motivo || '').length > LIMITE_TEXTO) erros.push(`O motivo do diagnóstico passa de ${LIMITE_TEXTO} letras.`);
  }
  if (mexeu('motivo_ganho', 'motivo_perda')) {
    if (linha.status === 'ganho' && (ef.motivo_ganho || '').length > LIMITE_TEXTO) erros.push(`O motivo passa de ${LIMITE_TEXTO} letras.`);
    if (linha.status === 'perdido') {
      if ((ef.motivo_perda || '').length > LIMITE_TEXTO) erros.push(`O motivo passa de ${LIMITE_TEXTO} letras.`);
      if (vazioNulo(ef.motivo_perda) === OUTRO) erros.push('Em "Outro motivo", escreva o detalhe.');
    }
  }
  return erros;
}

/** As três colunas à mão estão preenchidas de um jeito que o servidor aceitaria. */
export function linhaCompleta(l: LinhaBase): boolean {
  if (!vazioNulo(l.situacao_cliente)) return false;
  if (!l.diagnostico) return false;
  if (l.diagnostico === 'houve' && !dia(l.diagnostico_data)) return false;
  if (l.diagnostico === 'dispensada' && !vazioNulo(l.diagnostico_motivo)) return false;
  const porque = l.status === 'ganho' ? l.motivo_ganho : l.motivo_perda;
  return !!vazioNulo(porque);
}

export interface ResumoPropostas {
  total: number; completas: number;
  comReuniao: { n: number; ganhas: number }; semReuniao: { n: number; ganhas: number };
  dispensadas: number; semResposta: number;
}
/** "Com reunião" = diagnóstico 'houve'; "sem reunião" = 'nao_houve'. Dispensadas e em branco ficam fora das duas. */
export function resumoPropostas(linhas: LinhaBase[]): ResumoPropostas {
  const com = linhas.filter((l) => l.diagnostico === 'houve');
  const sem = linhas.filter((l) => l.diagnostico === 'nao_houve');
  const ganhas = (v: LinhaBase[]) => v.filter((l) => l.status === 'ganho').length;
  return {
    total: linhas.length, completas: linhas.filter(linhaCompleta).length,
    comReuniao: { n: com.length, ganhas: ganhas(com) }, semReuniao: { n: sem.length, ganhas: ganhas(sem) },
    dispensadas: linhas.filter((l) => l.diagnostico === 'dispensada').length,
    semResposta: linhas.filter((l) => !l.diagnostico).length,
  };
}

/** "http://" ou "https://" — a mesma exigência do banco. */
export const urlValida = (u: string): boolean => /^https?:\/\/\S+$/i.test(u.trim()) && u.trim().length <= 1000;
